// Pure audience-selection helpers shared by the drip wizard and its audience
// step. Kept free of component/runtime deps so both can import them and jest can
// unit-test the rules without loading either component's chain.
//
// A drip targets N audience boards. `Drip.boardIDArray` is `(string|number)[]`
// per the bt-common schema (Mongoose persists an ObjectId as either, depending on
// the write path), so every read coerces at the boundary.

/**
 * Normalise a persisted `boardIDArray` into the wizard's audience-id list:
 * stringified, blanks dropped, deduped, first-seen order preserved.
 *
 * Order is preserved because it is the order the user ticked, which is what the
 * review step and the derived campaign name read back.
 */
export const toAudienceIds = (boardIDArray?: readonly (string | number)[]): string[] => {
    const out: string[] = [];
    for (const raw of boardIDArray ?? []) {
        if (raw === null || raw === undefined) continue;
        const id = String(raw);
        if (!id || out.includes(id)) continue;
        out.push(id);
    }
    return out;
};

/**
 * Set-equality over two audience-id lists.
 *
 * Order-insensitive on purpose: the audience step mirrors the reused campaign
 * list's transient `newCampaign.boardIDArray` back up into the draft, and a
 * mirror that treated a reorder as a change would patch → re-seed → patch in a
 * loop.
 */
export const sameAudienceIds = (a: readonly string[], b: readonly string[]): boolean => {
    if (a.length !== b.length) return false;
    const seen = new Set(a);
    return b.every((id) => seen.has(id));
};

/** Just enough of i18next's `t` for a pure helper to be translatable. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * A human label for the chosen audiences, used for the auto-derived campaign name
 * ("Campaign to {label}") when the user never typed one.
 *
 * Names the first two outright, then counts the tail — "VIP Buyers and 2 others"
 * stays readable where a full join would not.
 *
 * `t` is REQUIRED, and that is the point. This helper used to build English
 * grammar in code — `${a} and ${b}`, `${a} and ${n} others` — which meant a
 * Hebrew user's campaign was silently named with an English conjunction, in the
 * wrong reading direction, and no translator could ever reach it. The word order
 * belongs to the locale, so the locale gets a template with both slots in it and
 * decides where they go. Making the parameter optional would have left every
 * existing call site quietly wrong; a required one makes the compiler find them.
 */
export const deriveAudienceLabel = (
    names: readonly (string | undefined)[],
    t: Translate,
): string | undefined => {
    const clean = names.map((n) => n?.trim()).filter((n): n is string => Boolean(n));
    if (clean.length === 0) return undefined;
    if (clean.length === 1) return clean[0];
    if (clean.length === 2) {
        return t('campaignsRedesign.audienceNames.pair', { first: clean[0], second: clean[1] });
    }
    // `count` is the SIZE OF THE TAIL, not the number of audiences: it is what the
    // sentence is counting ("and 2 others"), so it is what drives the plural form.
    return t('campaignsRedesign.audienceNames.andOthers', {
        first: clean[0],
        count: clean.length - 1,
    });
};

/**
 * Sum the per-board contact counts for a set of audience boards.
 *
 * `taskSumByBoardId` is `getTaskSumByBoardIdRecord` — a record keyed by the
 * audience boards CURRENTLY IN THE STORE. A board that hasn't been fetched has
 * no entry at all, so an absent key means "unknown", not "empty"; it contributes
 * 0 and the caller must make sure it fetched the boards it is about to count.
 */
export const sumAudienceContacts = (
    boardIds: readonly string[],
    taskSumByBoardId: Readonly<Record<string, number>>,
): number => boardIds.reduce((total, id) => total + (taskSumByBoardId[id] ?? 0), 0);

/**
 * The audience size to show for a (step-)campaign: how many recipients it
 * started with.
 *
 * Two sources, in order:
 *  1. `frozenContactCount` — the step-campaign's `contactCount`, stamped AT
 *     DISPATCH. Authoritative once present: it's who was actually sent to, and
 *     it doesn't drift when the source board is later edited or deleted.
 *  2. the live audience board(s) task sum — the only source for a campaign that
 *     hasn't dispatched yet (no `contactCount` exists), and for legacy campaigns
 *     that predate the field.
 *
 * `0` is treated as "not stamped" deliberately: the launcher writes the count
 * on dispatch, so a 0 here is an unstamped/legacy doc rather than a real
 * empty audience (an empty audience can't be launched — the wizard's
 * `getIsCampaignWithoutContacts` gate blocks it).
 */
export const deriveAudienceStart = (
    frozenContactCount: number | null | undefined,
    boardIds: readonly string[],
    taskSumByBoardId: Readonly<Record<string, number>>,
): number => {
    if (frozenContactCount != null && frozenContactCount > 0) return frozenContactCount;
    return sumAudienceContacts(boardIds, taskSumByBoardId);
};

/**
 * The custom-column titles a personalization token must match to resolve for
 * EVERY selected audience — the INTERSECTION across the boards, not the union.
 * A column present on only some boards would ship its token literally to the
 * recipients coming from the others, which is what the pre-launch validator
 * exists to catch.
 *
 * Matched on the validator's own normalized key (case- + whitespace-insensitive,
 * mirroring `normalizeTokenKey`), so boards spelling the same column `Company` /
 * `company` / `Com pany` still intersect. An exact-string match would drop it and
 * falsely flag a token that resolves fine — blocking a launch it shouldn't. The
 * first board's spelling is returned as the representative; the validator
 * re-normalizes anyway.
 *
 * An EMPTY per-board entry means "columns not loaded yet" and is ignored: the
 * wizard fetches them per board, and letting an in-flight board empty the
 * intersection would flag every custom token mid-load. A board with genuinely no
 * custom columns is indistinguishable — but it contributes no titles either way,
 * so the only cost is that the flag lands when its columns arrive.
 */
export const intersectColumnTitles = (perBoard: readonly (readonly string[])[]): string[] => {
    const loaded = perBoard.filter((titles) => titles.length > 0);
    if (loaded.length === 0) return [];
    const normalize = (title: string) => title.toLowerCase().replace(/\s+/g, '');
    const keySets = loaded.map((titles) => new Set(titles.map(normalize)));
    const out: string[] = [];
    const taken = new Set<string>();
    for (const title of loaded[0]) {
        const key = normalize(title);
        if (!key || taken.has(key)) continue;
        if (keySets.every((set) => set.has(key))) {
            taken.add(key);
            out.push(title);
        }
    }
    return out;
};
